import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Alert } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle, ArrowLeft, ArrowRight, X, Pencil, Check, Trash2 } from 'lucide-react';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import './ComponentValidation.css';
import { Building } from '../../../types/protocols';
import { BaseComponentImpl } from '../../../types/implementations';
import { BASE_TYPE_GROUPS } from '../../../Helpers/baseTypeUtils';
import { translate } from '../../../types/EnumTranslations';
import { getInvalidComponents, getValidationStats } from '../MaintenanceDraft/utils/componentValidation';
import { loadBuilding } from '../MaintenanceDraft/services/firebaseService';
import { maintenanceTemplateApi } from '../../../api/maintenanceTemplateApi';
import { componentFieldsApi, ComponentFieldPatch } from '../../../api/componentFieldsApi';
import { useBuildComponentTypes } from '../../../contexts/BuildComponentTypesContext';
import { getCategoryField } from '../../../Helpers/componentTypeCategories';
import { resolveComponentLocation, resolveParentSurface } from '../../../Helpers/ComponentRoomUtils';
import { useIsAdmin } from '../../../hooks/useIsAdmin';
import { SpecificTypeModal, SpecificTypeSelection } from '../MaintenanceDraft/components/modals/SpecificTypeModal';
import { ValidationPhotos } from './ValidationPhotos';
import { ValidationLocations } from './ValidationLocations';
import { DeleteComponentModal } from './DeleteComponentModal';
import LifetimeEditModal from '../TemplatesEditor/LifetimeEditModal';
import { ComponentLifetime } from '../TemplatesEditor/types';
import { BreadcrumbComponent } from '../../../Components/BreadCrumb';

// Format years remaining for display (Issue #10: simplified version for inline display)
// Note: The full calculateYearsRemaining in calculations.ts accounts for time since assessment,
// but here we're setting new conditions so we use a simpler formula
const formatYearsRemaining = (condition: number, expectedLifetimeYears: number): string => {
    const years = Math.floor((condition / 10) * expectedLifetimeYears);
    return years < 1 ? 'Mindre än ett år kvar' : `${years} år kvar`;
};

// Condition and years left, always both. Years left needs the type's expected
// lifetime; without one the label says why it is missing, and a type without
// a lifetime can be given one from here
const ConditionSummary: React.FC<{
    condition: number;
    expectedLifetime: number | undefined;
    hasType: boolean;
    onEditLifetime?: () => void;
}> = ({ condition, expectedLifetime, hasType, onEditLifetime }) => (
    <>
        <span className="condition-value">Skick {condition.toFixed(1)}/10</span>
        {expectedLifetime ? (
            <span className="condition-years">{formatYearsRemaining(condition, expectedLifetime)}</span>
        ) : hasType && onEditLifetime ? (
            <button
                type="button"
                className="condition-years unknown condition-years-action"
                onClick={onEditLifetime}
                title="Ange livslängd för typen"
            >
                Typen saknar livslängd
            </button>
        ) : (
            <span className="condition-years unknown">
                {hasType ? 'Typen saknar livslängd' : 'Välj typ för år kvar'}
            </span>
        )}
    </>
);

// Condition cell component - extracted from IIFE (Issue #11)
const ConditionCell = React.memo<{
    hasCondition: boolean;
    existingCondition: number | undefined;
    currentCondition: number | undefined;
    expectedLifetime: number | undefined;
    typeKey: string | undefined;
    /** False when the type key points to no type in config (nothing to give a lifetime) */
    typeExists: boolean;
    onConditionChange: (value: number) => void;
    onEditLifetime: (typeKey: string) => void;
}>(({ hasCondition, existingCondition, currentCondition, expectedLifetime, typeKey, typeExists, onConditionChange, onEditLifetime }) => {
    // Local state for slider to prevent re-renders during drag (Issue #7)
    const [localValue, setLocalValue] = useState<number | undefined>(currentCondition);
    const isDragging = useRef(false);
    const hasType = !!typeKey;
    const editLifetime = typeKey && typeExists ? () => onEditLifetime(typeKey) : undefined;

    // Sync local value when external value changes (but not during drag)
    useEffect(() => {
        if (!isDragging.current) {
            setLocalValue(currentCondition);
        }
    }, [currentCondition]);

    if (hasCondition) {
        return (
            <span className="condition-set">
                <ConditionSummary
                    condition={existingCondition || 0}
                    expectedLifetime={expectedLifetime}
                    hasType={hasType}
                    onEditLifetime={editLifetime}
                />
            </span>
        );
    }

    const displayValue = localValue ?? 5;
    // Also while dragging the first time, before the value is committed
    const hasValue = localValue !== undefined;

    return (
        <div className="condition-slider-wrapper">
            <div className="condition-slider-container">
                <div
                    className="condition-slider-track"
                    style={{
                        background: hasValue
                            ? `linear-gradient(to right,
                                #dc3545 0%,
                                #fd7e14 25%,
                                #ffc107 50%,
                                #198754 75%,
                                #198754 100%)`
                            : '#dee2e6'
                    }}
                />
                <input
                    type="range"
                    className="condition-slider"
                    min={0}
                    max={10}
                    step={0.1}
                    value={displayValue}
                    onChange={(e) => {
                        isDragging.current = true;
                        setLocalValue(parseFloat(e.target.value));
                    }}
                    onMouseUp={() => {
                        isDragging.current = false;
                        if (localValue !== undefined) {
                            onConditionChange(localValue);
                        }
                    }}
                    onTouchEnd={() => {
                        isDragging.current = false;
                        if (localValue !== undefined) {
                            onConditionChange(localValue);
                        }
                    }}
                />
            </div>
            <span className={`condition-label ${hasValue ? 'has-value' : ''}`}>
                {hasValue
                    ? <ConditionSummary condition={displayValue} expectedLifetime={expectedLifetime} hasType={hasType} onEditLifetime={editLifetime} />
                    : 'Dra för att ange'}
            </span>
        </div>
    );
});

// Pending changes for a component
interface PendingChange {
    condition?: number;
    /** '' when a category-only save cleared the type */
    specificType?: string;
    /** What the type modal has written this session (type, category), so reopening it shows the saved state */
    typePatch?: ComponentFieldPatch;
}

const ComponentValidation: React.FC = () => {
    const { buildingKey } = useParams<{ buildingKey: string }>();
    const { types: buildComponentTypes, refresh: refreshTypes } = useBuildComponentTypes();

    const [building, setBuilding] = useState<Building | null>(null);
    useDocumentTitle(`Validera komponenter - ${building?.summary?.name || 'Byggnad'}`);

    const [loading, setLoading] = useState<boolean>(true);
    const isAdmin = useIsAdmin();
    const [error, setError] = useState<string | null>(null);
    const [typeModalComponent, setTypeModalComponent] = useState<BaseComponentImpl | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<BaseComponentImpl | null>(null);
    // Type whose lifetime is being set from "Typen saknar livslängd"
    const [lifetimeTypeKey, setLifetimeTypeKey] = useState<string | null>(null);
    const [savingLifetime, setSavingLifetime] = useState<boolean>(false);
    const [pendingChanges, setPendingChanges] = useState<Record<string, PendingChange>>({});

    // Inline name (label) editing — committed immediately, independent of the batch save
    const [editingNameKey, setEditingNameKey] = useState<string | null>(null);
    const [nameInput, setNameInput] = useState<string>('');
    const [savingNameKey, setSavingNameKey] = useState<string | null>(null);

    // Load building data
    useEffect(() => {
        if (!buildingKey) return;

        const loadBuildingData = async () => {
            setLoading(true);
            try {
                const buildingData = await loadBuilding(buildingKey);
                if (buildingData) {
                    setBuilding(buildingData);
                }
            } catch (error) {
                console.error('Error loading building:', error);
            } finally {
                setLoading(false);
            }
        };

        loadBuildingData();
    }, [buildingKey]);

    const invalidComponents = useMemo(() =>
        getInvalidComponents(building, buildComponentTypes),
        [building, buildComponentTypes]
    );

    const stats = getValidationStats(building, buildComponentTypes);

    // Check if a component has complete pending changes
    const isComponentComplete = useCallback((componentKey: string, validation: { hasCondition: boolean; hasSpecificType: boolean }) => {
        const pending = pendingChanges[componentKey];
        const hasCondition = validation.hasCondition || (pending?.condition !== undefined);
        const hasSpecificType = pending?.specificType !== undefined
            ? !!pending.specificType
            : validation.hasSpecificType;
        return hasCondition && hasSpecificType;
    }, [pendingChanges]);

    // Components still needing input (locally, including this session's optimistic changes)
    const remainingCount = useMemo(() => {
        return invalidComponents.filter(({ component, validation }) =>
            !isComponentComplete(component.key, validation)
        ).length;
    }, [invalidComponents, isComponentComplete]);

    // True once every listed component has been filled in this session
    const allLocallyComplete = invalidComponents.length > 0 && remainingCount === 0;

    // Persist a condition value to Firebase immediately
    const persistCondition = useCallback(async (componentKey: string, value: number) => {
        if (!buildingKey) return;
        try {
            await componentFieldsApi.updateCondition(buildingKey, componentKey, value);
        } catch (error) {
            console.error('Error saving condition:', error);
            setError('Kunde inte spara skicket. Försök igen.');
        }
    }, [buildingKey]);

    // Handle condition change — optimistic UI update + immediate save
    const handleConditionChange = useCallback((componentKey: string, value: number) => {
        setPendingChanges(prev => ({
            ...prev,
            [componentKey]: {
                ...prev[componentKey],
                condition: value
            }
        }));
        persistCondition(componentKey, value);
    }, [persistCondition]);

    // Open the "Ange typ" modal on the component as saved so far this session
    const handleOpenTypeModal = useCallback((component: BaseComponentImpl) => {
        const typePatch = pendingChanges[component.key]?.typePatch;
        setTypeModalComponent(typePatch ? new BaseComponentImpl({ ...component, ...typePatch }) : component);
    }, [pendingChanges]);

    // Save from the "Ange typ" modal (pick or create a type; for Utility/Outdoor
    // the category is saved with it). Saved immediately; errors are shown by the modal.
    const handleTypeModalSave = useCallback(async (selection: SpecificTypeSelection) => {
        if (!buildingKey || !typeModalComponent) return;
        const component = typeModalComponent;
        const categoryField = getCategoryField(component.type);
        const patch = categoryField && selection.category
            ? await componentFieldsApi.setTypeAndCategory(
                buildingKey,
                component.key,
                selection.typeKey,
                selection.category,
                categoryField,
                buildComponentTypes
            )
            : await componentFieldsApi.updateField(buildingKey, component.key, 'specificType', selection.typeKey);

        // A category-only save leaves the type alone unless it had to be cleared
        setPendingChanges(prev => ({
            ...prev,
            [component.key]: {
                ...prev[component.key],
                ...('specificType' in patch ? { specificType: selection.typeKey || '' } : {}),
                typePatch: { ...prev[component.key]?.typePatch, ...patch }
            }
        }));
    }, [buildingKey, typeModalComponent, buildComponentTypes]);

    const handleSaveLifetime = useCallback(async (lifetime: ComponentLifetime) => {
        if (!lifetimeTypeKey) return;
        setSavingLifetime(true);
        try {
            await maintenanceTemplateApi.saveLifetime(lifetimeTypeKey, lifetime, { source: 'draft', buildingKey });
            // Years left reads the lifetime from the types context
            await refreshTypes();
            setLifetimeTypeKey(null);
        } finally {
            // A rejection is shown by LifetimeEditModal, which stays open
            setSavingLifetime(false);
        }
    }, [lifetimeTypeKey, refreshTypes]);

    // Drop a deleted component from the loaded building
    const handleComponentDeleted = useCallback((componentKey: string) => {
        setBuilding(prev => {
            if (!prev?.model?.structure?.components) return prev;
            const components = { ...prev.model.structure.components };
            delete components[componentKey];
            return {
                ...prev,
                model: { ...prev.model, structure: { ...prev.model.structure, components } }
            };
        });
        setDeleteTarget(null);
    }, []);

    // Start editing a component's custom name (label)
    const handleStartEditName = useCallback((componentKey: string, currentLabel: string) => {
        setEditingNameKey(componentKey);
        setNameInput(currentLabel);
    }, []);

    // Save the edited name to Firebase immediately and refresh the building
    const handleSaveName = useCallback(async (componentKey: string) => {
        if (!buildingKey) return;
        const trimmed = nameInput.trim();
        setSavingNameKey(componentKey);
        try {
            await componentFieldsApi.updateField(buildingKey, componentKey, 'label', trimmed || null);
            const buildingData = await loadBuilding(buildingKey);
            if (buildingData) {
                setBuilding(buildingData);
            }
            setEditingNameKey(null);
        } catch (error) {
            console.error('Error saving component name:', error);
            setError('Kunde inte spara namnet. Försök igen.');
        } finally {
            setSavingNameKey(null);
        }
    }, [buildingKey, nameInput]);

    // Group components
    const groupedComponents = useMemo(() => {
        const coveredBaseTypes = new Set(BASE_TYPE_GROUPS.flatMap(group => group.baseTypes));

        const groups = BASE_TYPE_GROUPS.map(group => ({
            ...group,
            components: invalidComponents.filter(({ component }) =>
                group.baseTypes.includes(component.type)
            )
        })).filter(group => group.components.length > 0);

        // Add uncovered types
        const unmatchedComponents = invalidComponents.filter(({ component }) =>
            !coveredBaseTypes.has(component.type)
        );

        if (unmatchedComponents.length > 0) {
            const unmatchedByType = new Map<string, typeof invalidComponents>();
            unmatchedComponents.forEach(item => {
                const typeKey = item.component.type;
                if (!unmatchedByType.has(typeKey)) {
                    unmatchedByType.set(typeKey, []);
                }
                unmatchedByType.get(typeKey)!.push(item);
            });

            unmatchedByType.forEach((components, baseType) => {
                groups.push({
                    key: `other_${baseType}`,
                    label: translate(baseType as any),
                    baseTypes: [baseType as any],
                    components
                });
            });
        }

        return groups;
    }, [invalidComponents]);

    const allValid = invalidComponents.length === 0;

    if (!buildingKey) {
        return (
            <div className="alert alert-warning m-4">
                <h4>Byggnadsnyckel saknas</h4>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="loading-container">
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status" />
                    <h5 className="text-muted">Laddar komponentvalidering...</h5>
                </div>
            </div>
        );
    }

    if (!building) {
        return (
            <div className="alert alert-warning m-4">
                <h4>Byggnad hittades inte</h4>
            </div>
        );
    }

    return (
        <div className="component-validation-page">
            <BreadcrumbComponent items={[{ text: building.summary?.name || 'Byggnad', link: `/building/${buildingKey}` }, { text: 'Validera komponenter' }]} />
            {/* Header */}
            <div className="validation-header">
                <div className="validation-header-left">
                    <Link
                        to={`/building/${buildingKey}`}
                        className="btn btn-outline-secondary btn-sm"
                    >
                        <ArrowLeft size={16} className="me-1" />
                        Tillbaka
                    </Link>
                    <div className="validation-title">
                        <h1 className="page-title">Validera komponenter</h1>
                        <span className="text-muted">{building.summary?.name}</span>
                    </div>
                </div>
                <div className="validation-stats-header">
                    <span className="stat-badge stat-total">{stats.total} totalt</span>
                    <span className="stat-badge stat-valid">{stats.valid + (invalidComponents.length - remainingCount)} klara</span>
                    <span className="stat-badge stat-invalid">{remainingCount} kvar</span>
                </div>
            </div>

            {/* Success state */}
            {allValid ? (
                <div className="validation-success">
                    <CheckCircle size={48} className="text-success mb-3" />
                    <h2>Alla komponenter är validerade!</h2>
                    <p className="text-muted mb-4">Du kan nu fortsätta till underhållsplanen.</p>
                    <Link
                        to={`/maintenanceDrafts/${buildingKey}`}
                        className="btn btn-success btn-lg"
                    >
                        Fortsätt till underhållsplan
                        <ArrowRight size={20} className="ms-2" />
                    </Link>
                </div>
            ) : (
                <div className="validation-content">
                    {/* Component list */}
                    <div className="validation-list-container">
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}
                        {groupedComponents.map(group => (
                            <div key={group.key} className="validation-group">
                                <div className="validation-group-header">
                                    <span className="group-label">{group.label}</span>
                                    <span className="group-count">{group.components.length}</span>
                                </div>
                                <div className="validation-rows">
                                    <div className="validation-header-row">
                                        <span>Komponent</span>
                                        <span>Placering</span>
                                        <span>Typ</span>
                                        <span>Skick</span>
                                        <span>Foton</span>
                                        <span>Position</span>
                                        <span></span>
                                    </div>
                                    {group.components.map(({ component, validation }) => {
                                        const pending = pendingChanges[component.key];
                                        const isComplete = isComponentComplete(component.key, validation);

                                        const currentCondition = pending?.condition ??
                                            (validation.hasCondition ? component.condition?.value?.value : undefined);
                                        const currentType = pending?.specificType ??
                                            (validation.hasSpecificType ? component.specificType?.value : '') ?? '';

                                        // Get expected lifetime from the specific type
                                        const typeKey = currentType || component.specificType?.value;
                                        const expectedLifetime = typeKey
                                            ? buildComponentTypes[typeKey]?.maintenance?.lifetime?.expectedLifetimeYears
                                            : undefined;

                                        // Resolve placement (floor + room) through the parent hierarchy
                                        const location = resolveComponentLocation(component, building.model.structure);
                                        const hasPlacement = !!(location.levelDisplay || location.roomDisplay);

                                        // Secondary placement: when there's no room, fall back to the
                                        // surface the component sits on (e.g. the facade a window is placed on)
                                        const parentSurface = hasPlacement
                                            ? null
                                            : resolveParentSurface(component, building);

                                        return (
                                            <div
                                                key={component.key}
                                                className={`validation-row ${isComplete ? 'complete' : ''}`}
                                            >
                                                {/* Component name (with inline pencil edit) */}
                                                <div className="validation-row-name">
                                                    {editingNameKey === component.key ? (
                                                        <div className="name-edit">
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                value={nameInput}
                                                                autoFocus
                                                                placeholder="Ange eget namn"
                                                                disabled={savingNameKey === component.key}
                                                                onChange={(e) => setNameInput(e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') handleSaveName(component.key);
                                                                    if (e.key === 'Escape') setEditingNameKey(null);
                                                                }}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="name-edit-btn"
                                                                title="Spara namn"
                                                                disabled={savingNameKey === component.key}
                                                                onClick={() => handleSaveName(component.key)}
                                                            >
                                                                <Check size={16} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="name-edit-btn"
                                                                title="Avbryt"
                                                                disabled={savingNameKey === component.key}
                                                                onClick={() => setEditingNameKey(null)}
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="name-display">
                                                            <a
                                                                className="name-text name-link"
                                                                href={`/building/${buildingKey}/component/${component.key}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title={component.displayName(building)}
                                                            >
                                                                {component.displayName(building)}
                                                            </a>
                                                            <button
                                                                type="button"
                                                                className="name-edit-btn"
                                                                title="Redigera namn"
                                                                onClick={() => handleStartEditName(component.key, component.label?.value || '')}
                                                            >
                                                                <Pencil size={14} />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Placering — room first, else the surface it's placed on */}
                                                <div className="validation-row-placement">
                                                    {hasPlacement ? (
                                                        <>
                                                            {location.levelDisplay && location.levelKey && (
                                                                <a
                                                                    className="placement-level placement-link"
                                                                    href={`/building/${buildingKey}/level/${location.levelKey}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    {location.levelDisplay}
                                                                </a>
                                                            )}
                                                            {location.roomDisplay && location.roomKey && (
                                                                <a
                                                                    className="placement-room placement-link"
                                                                    href={`/building/${buildingKey}/room/${location.roomKey}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    {location.roomDisplay}
                                                                </a>
                                                            )}
                                                        </>
                                                    ) : parentSurface ? (
                                                        <a
                                                            className="placement-parent placement-link"
                                                            href={`/building/${buildingKey}/component/${parentSurface.key}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {parentSurface.name}
                                                        </a>
                                                    ) : (
                                                        <span className="placement-empty">–</span>
                                                    )}
                                                </div>

                                                {/* Type — opens the "Ange typ" modal; editable also when
                                                    a type is already set (user can correct it) */}
                                                <div className="validation-row-type">
                                                    <button
                                                        type="button"
                                                        className={`type-button ${currentType ? 'has-value' : ''}`}
                                                        title={currentType ? 'Ändra typ' : 'Ange typ'}
                                                        onClick={() => handleOpenTypeModal(component)}
                                                    >
                                                        <span className="type-button-text">
                                                            {buildComponentTypes[currentType]?.translations.sv || 'Välj typ...'}
                                                        </span>
                                                        <Pencil size={14} aria-hidden="true" />
                                                    </button>
                                                </div>

                                                {/* Condition slider (Issue #11: extracted component) */}
                                                <div className="validation-row-condition">
                                                    <ConditionCell
                                                        hasCondition={validation.hasCondition}
                                                        existingCondition={component.condition?.value?.value}
                                                        currentCondition={currentCondition}
                                                        expectedLifetime={expectedLifetime}
                                                        typeKey={typeKey || undefined}
                                                        typeExists={!!(typeKey && buildComponentTypes[typeKey])}
                                                        onConditionChange={(value) => handleConditionChange(component.key, value)}
                                                        onEditLifetime={setLifetimeTypeKey}
                                                    />
                                                </div>

                                                {/* Every photo; a click opens the component's gallery */}
                                                <div className="validation-row-photos">
                                                    <ValidationPhotos photos={component.photos} buildingKey={buildingKey} />
                                                </div>

                                                {/* Pins in photos, on the drawing and on the map */}
                                                <div className="validation-row-locations">
                                                    <ValidationLocations component={component} building={building} />
                                                </div>

                                                {/* Status indicator and delete */}
                                                <div className="validation-row-status">
                                                    {isComplete && (
                                                        <CheckCircle size={18} className="text-success" />
                                                    )}
                                                    {isAdmin && (
                                                        <button
                                                            type="button"
                                                            className="validation-delete-btn"
                                                            title="Ta bort komponent"
                                                            aria-label={`Ta bort ${component.displayName(building)}`}
                                                            onClick={() => setDeleteTarget(component)}
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Changes save automatically; continue once everything is filled in */}
            {!allValid && allLocallyComplete && (
                <Link
                    to={`/maintenanceDrafts/${buildingKey}`}
                    className="validation-save-button"
                >
                    Fortsätt till underhållsplan
                    <ArrowRight size={20} className="ms-2" />
                </Link>
            )}

            {/* Same modal as "Ange typ" on the component page, incl. "Skapa ny typ" */}
            {typeModalComponent && (
                <SpecificTypeModal
                    component={typeModalComponent}
                    building={building}
                    onSave={handleTypeModalSave}
                    onClose={() => setTypeModalComponent(null)}
                />
            )}

            {lifetimeTypeKey && (
                <LifetimeEditModal
                    show
                    lifetime={buildComponentTypes[lifetimeTypeKey]?.maintenance?.lifetime}
                    componentType={lifetimeTypeKey}
                    onSave={handleSaveLifetime}
                    onCancel={() => setLifetimeTypeKey(null)}
                    saving={savingLifetime}
                />
            )}

            {deleteTarget && (
                <DeleteComponentModal
                    component={deleteTarget}
                    building={building}
                    buildingKey={buildingKey}
                    onDeleted={handleComponentDeleted}
                    onClose={() => setDeleteTarget(null)}
                />
            )}
        </div>
    );
};

export default ComponentValidation;
